import { useEffect } from 'react';

import { useSuperState } from 'hooks';
import { useIntl } from 'react-intl';
import { useApp } from 'app/context';
import ReactDOM from 'react-dom';

/// import { UsersApi } from 'apis';

import 'components/SupportPillWidget/SupportPillWidget.scss';

// The pill always starts collapsed on every page load, regardless of the saved
// preference. The preference read/write is commented out (not removed) so per-user
// persistence can be restored later.
//
// Persist the collapsed state per-user via the existing page-preference claim
// mechanism (same store behind "Reset Site Preferences"), so it follows the admin
// across machines and never leaves per-browser localStorage trash. Default (no
// claim) resolves to "Show" server-side, i.e. the pill is visible for new users.
/// const SUPPORT_PILL_WIDGET_PREFERENCE = 'http://schemas.gmetrix.net/claims/Admin/Preference/PagePreference/SupportWidgetPillShowHide';

// The Freshworks loader (in Default.aspx) sets this flag only on the GMetrix
// domain, top-level frame, once the widget shim is queued.
type FreshworksWindow = Window & {
    __gmSupportEnabled?: boolean;
    FreshworksWidget?: (...args: unknown[]) => void;
};

export const SupportPillWidget = () => {

    const fwWindow = window as FreshworksWindow;

    const { formatMessage } = useIntl();

    /// const { currentUser, supportPillWidgetCollapsed } = useApp();
    const { currentUser } = useApp();

    const [state, setState] = useSuperState({
        collapsed: true, /// !!supportPillWidgetCollapsed
        enabled: false
    });

    const { collapsed, enabled } = state;

    const supportLabel = formatMessage({ id: 'support.f39b85ae', defaultMessage: 'Support' });

    const setCollapsed = (value: boolean) => {

        setState({ collapsed: value });

        // Fire-and-forget, mirroring how the data grids persist their prefs.
        /// UsersApi.SetPagePreference(SUPPORT_PILL_WIDGET_PREFERENCE, value ? 'Hide' : 'Show');

    };

    const openWidget = () => {

        fwWindow.FreshworksWidget?.('open');

    };

    useEffect(() => {

        // Only surface on the top-level GMetrix shell where the widget was loaded,
        // and only once a user is actually logged in. The pill stays hidden until
        // then and renders straight into its collapsed state (no flash of the
        // expanded pill, and nothing on the login page).
        const isEnabled = !!fwWindow.__gmSupportEnabled
            && !!currentUser
            && window.location === window.parent.location;
        if (!isEnabled) return;

        /// setState({ collapsed: !!supportPillWidgetCollapsed, enabled: true });
        setState({ enabled: true });

        // Suppress the native launcher; our custom button drives the widget.
        fwWindow.FreshworksWidget?.('hide', 'launcher');

    }, [currentUser]); /// [currentUser, supportPillWidgetCollapsed]

    const rootClass = `support-pill-widget ${collapsed ? 'support-pill-widget--collapsed' : 'support-pill-widget--expanded'}`;

    return (
        !enabled ? null : ReactDOM.createPortal(
            <div className={rootClass}>
                <div className="support-pill-widget__main">
                    <button aria-label={supportLabel} className="support-pill-widget__button" onClick={openWidget} type="button">
                        <i aria-hidden="true" className="fa-solid fa-circle-question" />
                        <span>
                            {supportLabel}
                        </span>
                    </button>
                    <span aria-hidden="true" className="support-pill-widget__divider" />
                    <button
                        aria-label={supportLabel}
                        className="support-pill-widget__close"
                        onClick={() => setCollapsed(true)}
                        title={formatMessage({ id: 'close.99e01fb5', defaultMessage: 'Close' })}
                        type="button"
                    >
                        <i aria-hidden="true" className="fa-solid fa-xmark" />
                    </button>
                </div>
                <button
                    aria-label={supportLabel}
                    className="support-pill-widget__tab"
                    onClick={() => setCollapsed(false)}
                    title={supportLabel}
                    type="button"
                >
                    <i aria-hidden="true" className="fa-solid fa-circle-question" />
                </button>
            </div>,
            document.body
        )
    );

};
